import axios from 'axios'
import React, { useState } from 'react'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	MenuItem,
	TextField,
	Typography
} from '@material-ui/core'
import { Add, AddCircleOutline } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import iziToast from 'izitoast'

import useIntl from 'hooks/useIntl'
import TextToolTip from 'components/TextToolTip'
import { useStoreon } from 'storeon/react'

interface SubSection {
	subSectionId: number
	subSectionName: string
}

interface SubSectionSelectProps {
	sectionData: { id: number | string; name: string }[]
	subSectionData: SubSection[]
	setSubSectionData: React.Dispatch<React.SetStateAction<SubSection[]>>
	selectedSubSection: string
	setSelectedSubSection: React.Dispatch<React.SetStateAction<string>>
	isCustomSubSection: boolean
	setIsCustomSubSection: React.Dispatch<React.SetStateAction<boolean>>
	customSubSection: string
	setCustomSubSection: React.Dispatch<React.SetStateAction<string>>
	selectedSection: string

	refetchSubSections: (sectionId: string) => void
}

const useStyles = makeStyles(theme => ({
	iconButton: {
		backgroundColor: '#7D6EF1',
		color: 'white',
		width: 40,
		height: 40,
		borderRadius: '50%',
		padding: 4,
		marginLeft: theme.spacing(0.5)
	}
}))

const SubSectionSelect: React.FC<SubSectionSelectProps> = ({
	sectionData,
	subSectionData,
	setSubSectionData,
	selectedSubSection,
	setSelectedSubSection,
	isCustomSubSection,
	setIsCustomSubSection,
	customSubSection,
	setCustomSubSection,
	refetchSubSections
}) => {
	const classes = useStyles()
	const [open, setOpen] = useState(false)
	const [newSubSection, setNewSubSection] = useState('')
	const [selectedSection, setSelectedSection] = useState('')
	const [description, setDescription] = useState('')
	const intl = useIntl()
	const { token } = useStoreon('token')
	const handleOpenModal = () => {
		setNewSubSection('')
		setSelectedSection('')
		setDescription('')
		setOpen(true)
	}

	const handleCloseModal = () => {
		setOpen(false)
	}
	const handleCreate = async () => {
		const userId = localStorage.getItem('userId')

		try {
			const response = await axios.post(
				`${process.env.REACT_APP_API_URL}/api/v1/custom/createSubSection`,
				{
					name: newSubSection,
					sectionId: selectedSection,
					description: description,
					createdBy: userId
				},
				{
					headers: {
						Authorization: ` ${token}`
					}
				}
			)

			if (response.data && response.data.message) {
				iziToast.success({
					message: response.data.message
				})
				refetchSubSections(selectedSection)

				handleCloseModal()
			} else {
				iziToast.error({
					message: 'Unexpected response from server.'
				})
			}
		} catch (error: any) {
			console.error('Error creating sub-section:', error)
			iziToast.error({
				message:
					error?.response?.data?.message ||
					error.message ||
					'Something went wrong while creating the sub-section.'
			})
		}

		setOpen(false)
	}

	return (
		<>
			<Grid>
				<Typography gutterBottom>
					{intl.formatMessage({ id: 'Sub-Section Name' })}
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<FormControl fullWidth>
						<TextField
							select
							fullWidth
							value={selectedSubSection}
							onChange={e => setSelectedSubSection(e.target.value)}
							SelectProps={{
								MenuProps: {
									getContentAnchorEl: null,
									anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
									transformOrigin: { vertical: 'top', horizontal: 'left' },
									PaperProps: {
										style: { maxHeight: 200, overflowY: 'auto' }
									}
								},
								displayEmpty: true
							}}
						>
							<MenuItem value="" disabled>
								Select a Sub-Section
							</MenuItem>
							{subSectionData.length > 0 ? (
								subSectionData.map(sub => {
									if (!sub.subSectionId) {
										console.warn('Invalid subSection object:', sub)
										return null
									}
									return (
										<MenuItem key={sub.subSectionId} value={sub.subSectionId.toString()}>
											{sub.subSectionName}
										</MenuItem>
									)
								})
							) : (
								<MenuItem disabled>No SubSections Available</MenuItem>
							)}
						</TextField>
					</FormControl>
					<TextToolTip title="Add Sub Section">
						<Add onClick={handleOpenModal} sx={{ color: '#7D6EF1', cursor: 'pointer' }} />
					</TextToolTip>
				</Box>
			</Grid>

			<Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="sm">
				<DialogTitle>{intl.formatMessage({ id: 'Add New SubSection' })}</DialogTitle>
				<DialogContent>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<FormControl fullWidth>
								<TextField
									select
									label={intl.formatMessage({ id: 'Section Name' })}
									value={selectedSection}
									onChange={e => setSelectedSection(e.target.value)}
									SelectProps={{
										MenuProps: {
											getContentAnchorEl: null,
											anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
											transformOrigin: { vertical: 'top', horizontal: 'left' },
											PaperProps: {
												style: { maxHeight: 200, overflowY: 'auto' }
											}
										},
										displayEmpty: true
									}}
								>
									<MenuItem value="" disabled>
										Select a Section
									</MenuItem>
									{sectionData.length > 0 ? (
										sectionData.map(section => (
											<MenuItem key={section.id} value={section.id.toString()}>
												{section.name}
											</MenuItem>
										))
									) : (
										<MenuItem disabled>No Sections Available</MenuItem>
									)}
								</TextField>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label={intl.formatMessage({ id: 'Sub-Section Name' })}
								placeholder="Enter subsection name"
								value={newSubSection}
								onChange={e => setNewSubSection(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label={intl.formatMessage({ id: 'Description' })}
								multiline
								rows={3}
								value={description}
								onChange={e => setDescription(e.target.value)}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseModal} color="secondary">
						{intl.formatMessage({ id: 'Cancel' })}
					</Button>
					<Button onClick={handleCreate} color="primary" variant="contained">
						{intl.formatMessage({ id: 'Create' })}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default SubSectionSelect
